import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import 'leaflet';
import 'leaflet-extra-markers';
import 'leaflet-polylinedecorator';
import 'leaflet-routing-machine';
import { Observable } from 'rxjs/Observable';

import {
  AlertcPoint,
  CBMError,
  Contract,
  ContractSettings,
  IntersectionData,
  QueueCharacteristics,
  Siterecord,
  SiterecordCharacteristics,
  SiterecordCharacteristicsExtended,
  SiterecordIntersections
} from 'app/models';
import {
  CarriagewayType,
  ComputationMethodType,
  EquipmentType,
  LaneType,
  LocationType,
  MeasurementCategoryType,
  MeasurementValueTypePoint,
  MeasurementValueTypePointVri,
  QueueDetectionMethodType,
  QueueType,
  SideType,
  SpecificLaneCompositionType,
  TrafficSortingCharacteristicsType
} from 'app/models/enums';
import { ContractService, ContractSettingsService, ErrorService, MapService, SiterecordService } from 'app/services';
import { MessageboxComponent } from 'app/shared/messagebox/messagebox.component';
import { enumValues } from 'app/shared/util/enumValues';

declare const L: any;

@Component({
  selector: 'app-measurement-point',
  templateUrl: './point-insert.component.html',
  styleUrls: ['./point-insert.component.scss']
})
export class PointInsertComponent implements OnInit {
  private map: any;
  private control: any;
  private layerControl: any;
  private line: any;
  private arrowHead: any;

  public lat: number;
  public lng: number;

  @ViewChild(MessageboxComponent) messagebox: MessageboxComponent;
  @ViewChild('locationForm') public form: NgForm = null;

  public location: Siterecord;
  public recordId: string;
  private contract: Contract;
  private contractSettings: ContractSettings;

  public siterecordIntersectionsTrafficlightSignalGroups: SiterecordIntersections[];
  public siterecordIntersectionsTrafficSortingCharacteristics: SiterecordIntersections[];
  public siterecordIntersectionsMaxLineupLengthAvailable: SiterecordIntersections[];
  public queueCharacteristicsQueueTypes: [QueueCharacteristics[]];
  public queueCharacteristicsQueueDetectionMethods: [QueueCharacteristics[]];
  public intersectionDataSpecificLaneCompositions: [IntersectionData[]];
  public intersectionDataTrafficSortingCharacteristics: [IntersectionData[]];

  public intersectionsTrafficSortingCharacteristics: string[];

  public isEquipmentTypeVRI: boolean;
  public isSaving = false;
  public newLocationAfterSave: boolean;
  public contractSettingsNotFound = true;

  public errorMessages: CBMError[];

  public prefixes: string[];
  public prefix: string;

  public computationMethods: string[];
  public equipmentTypes: string[];
  public sideTypes: string[];
  public carriagewayTypes: string[];
  public measurementValueTypes: string[];
  public measurementValueTypesVri: string[];
  public measurementCategoryTypes: string[];
  public specificLanes: string[];
  public queueTypes: string[];
  public queueDetectionMethodTypes: string[];
  public specificLaneCompositionTypes: string[];
  public trafficSortingCharacteristics: string[];

  private hectoPuntenLayer: any;
  private hectopuntenLayerTranslation: string;

  public previousWaypoints: any[];
  public noRouteFoundTranslation: string;
  public failedSaveTranslation: string;

  public singleColumnStyle = { label: 'col-xs-4', control: 'col-xs-8' };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly siterecordService: SiterecordService,
    private readonly contractService: ContractService,
    private readonly contractSettingsService: ContractSettingsService,
    private readonly mapService: MapService,
    private readonly translateService: TranslateService,
    private readonly errorService: ErrorService,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit() {
    this.errorService.pointInsertComponent = this;

    this.errorMessages = [];
    this.newLocationAfterSave = false;
    this.initialiseMap();
    this.initialiseErrorMessage();
    this.initialiseLayerNames();
    this.initialiseLayerControl();
    this.initialiseEnums();

    this.route.params.subscribe(params => {
      if (this.arrowHead && this.line) {
        this.map.removeLayer(this.arrowHead);
        this.map.removeLayer(this.line);
        this.map.removeControl(this.control);
      }

      const lat = params['lat'];
      const lng = params['lng'];

      if (lat && lng) {
        this.form.resetForm();
        this.map.setView([lat, lng], 12);
      } else {
        this.map.setView([this.mapService.defaultLat, this.mapService.defaultLng], this.mapService.defaultZoom);
      }

      if (!this.contract) {
        const contractId = params['contractId'];
        this.contractService.getContract(contractId).subscribe(contract => {
          this.contract = contract;
          this.initialiseNewLocation();
        });
      } else {
        this.initialiseNewLocation();
      }
    });

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.initialiseErrorMessage();
      this.initialiseLayerNames();
      this.initialiseLayerControl();
    });
  }

  private initialiseContractSettings(contractId: any): void {
    if (!this.contractSettings) {
      this.contractSettingsService.getContractSettings(contractId).subscribe(contractSettings => {
        this.contractSettings = contractSettings;
        if (this.contractSettings.pointComputationMethod) {
          this.location.computationMethod = this.contractSettings.pointComputationMethod;
        }
        if (this.contractSettings.pointAccuracy) {
          this.location.siterecordCharacteristics[0].accuracy = this.contractSettings.pointAccuracy;
        }
        if (this.contractSettings.pointPeriod) {
          this.location.siterecordCharacteristics[0].period = this.contractSettings.pointPeriod;
        }
        if (this.contractSettings.measurementCategory) {
          this.location.siterecordCharacteristics[0].measurementCategory = this.contractSettings.measurementCategory;
        }
      });
    }
  }

  private initialiseLayerNames(): void {
    this.translate('SITERECORD.HECTOPUNTENLAYER').subscribe(
      translation => (this.hectopuntenLayerTranslation = translation)
    );
  }
  private initialiseErrorMessage(): void {
    this.translate('ERROR.FE003').subscribe(translation => (this.noRouteFoundTranslation = translation));

    this.translate('ERROR.FE004').subscribe(translation => (this.failedSaveTranslation = translation));
  }

  private translate(valueToTranslate: string): Observable<string> {
    return this.translateService.get(valueToTranslate);
  }

  private initialiseLayerControl(): void {
    if (!this.layerControl) {
      this.layerControl = L.control.layers({}, {}, { position: 'topright' }).addTo(this.map);
    }
    this.layerControl.removeLayer(this.hectoPuntenLayer);
    this.layerControl.addOverlay(this.hectoPuntenLayer, this.hectopuntenLayerTranslation);
  }

  private initialiseMap() {
    this.map = L.map('map');

    L.tileLayer(this.mapService.osmTilesUrl, { maxZoom: 20, maxNativeZoom: 16 }).addTo(this.map);

    this.hectoPuntenLayer = L.tileLayer(this.mapService.hectometreTilesUrl, { maxZoom: 19 });
    this.hectoPuntenLayer.addTo(this.map);

    const greenMarker = L.ExtraMarkers.icon({
      markerColor: 'green',
      shape: 'circle'
    });

    this.control = L.Routing.control({
      router: new L.Routing.PgRouting('cbmdb', { serviceUrl: this.mapService.geoserverWfsUrl }),
      waypointMode: 'snap',
      autoRoute: false,
      createMarker(i, dStart, n) {
        if (i === 0) {
          return L.marker(dStart.latLng, {
            draggable: true,
            icon: greenMarker
          });
        }
      },
      routeLine(route) {
        return L.polyline(route.coordinates, {
          weight: 0
        });
      }
    }).addTo(this.map);

    this.control.on('routeselected', e => {
      const waypoints = this.control.getWaypoints();
      let routeFound = true;
      if (waypoints[0].latLng != null && waypoints[waypoints.length - 1].latLng != null) {
        this.lat = waypoints[0].latLng.lat;
        this.lng = waypoints[0].latLng.lng;
        this.line = L.polyline([e.route.coordinates[0], e.route.coordinates[1]], {}).addTo(this.map);
        this.arrowHead = L.polylineDecorator(this.line, {
          patterns: [
            {
              offset: '100%',
              repeat: 0,
              symbol: L.Symbol.arrowHead({ pixelSize: 15, polygon: false, pathOptions: { stroke: true } })
            }
          ]
        }).addTo(this.map);
      } else {
        routeFound = false;
      }

      if (routeFound) {
        this.previousWaypoints = [];
        for (const waypoint of waypoints) {
          this.previousWaypoints.push(L.latLng(waypoint.latLng.lat, waypoint.latLng.lng));
        }
        this.location.routingPoints = `${e.route.coordinates[0].lng}\\,${e.route.coordinates[0].lat}|${e.route.coordinates[1].lng}\\,${e.route.coordinates[1].lat}`;
      } else if (!routeFound) {
        this.errorFindingRoute();
      }
    });

    this.control.on('waypointschanged', e => {
      if (e.waypoints[0].latLng && e.waypoints[1].latLng && this.map.hasLayer(this.line)) {
        this.map.removeLayer(this.arrowHead);
        this.map.removeLayer(this.line);
        this.control.setWaypoints([L.latLng(e.waypoints[0].latLng.lat, e.waypoints[0].latLng.lng)]);
      }
      // Use ngZone to catch all leaflet and leaflet routing machine errors
      this.ngZone.runGuarded(() => this.control.route());
    });

    this.map.on('click', e => {
      if (!this.control.getWaypoints()[0].latLng) {
        this.control.setWaypoints([L.latLng(e.latlng.lat, e.latlng.lng)]);
      } else if (!this.control.getWaypoints()[1].latLng) {
        this.control.spliceWaypoints(this.control.getWaypoints().length - 1, 1, L.latLng(e.latlng));
      }

      this.control.on('routingerror', () => this.errorFindingRoute());
    });
  }

  public errorFindingRoute(): void {
    if (this.previousWaypoints && this.previousWaypoints.length > 0) {
      this.control.setWaypoints(this.previousWaypoints);
    }
    alert(this.noRouteFoundTranslation);
  }

  private initialiseEnums(): void {
    this.computationMethods = enumValues(ComputationMethodType);
    this.equipmentTypes = enumValues(EquipmentType);
    this.carriagewayTypes = enumValues(CarriagewayType);
    this.sideTypes = enumValues(SideType);

    this.specificLanes = enumValues(LaneType);
    this.measurementValueTypes = enumValues(MeasurementValueTypePoint);
    this.measurementValueTypesVri = enumValues(MeasurementValueTypePointVri);
    this.measurementCategoryTypes = enumValues(MeasurementCategoryType);

    this.queueTypes = enumValues(QueueType);
    this.queueDetectionMethodTypes = enumValues(QueueDetectionMethodType);

    this.specificLaneCompositionTypes = enumValues(SpecificLaneCompositionType);
    this.trafficSortingCharacteristics = enumValues(TrafficSortingCharacteristicsType);
  }

  private initialiseNewLocation(): void {
    this.location = new Siterecord();

    this.location.contractId = this.contract.id;
    this.location.locationType = LocationType[LocationType.point];
    this.location.routingPoints = '';
    this.location.active = false;

    this.location.alertcPoints = [new AlertcPoint()];
    this.location.siterecordCharacteristics = [];
    this.setNumberOfLanes();

    this.queueCharacteristicsQueueTypes = [[]];
    this.queueCharacteristicsQueueDetectionMethods = [[]];

    this.intersectionDataSpecificLaneCompositions = [[]];
    this.intersectionDataTrafficSortingCharacteristics = [[]];

    this.intersectionsTrafficSortingCharacteristics = [];

    this.isEquipmentTypeVRI = false;

    this.prefixes = this.contract.prefix.split(';');
    if (this.prefixes.length === 1) {
      this.setRecordIdPrefix(this.prefixes[0]);
    }
    this.addLane();

    this.initialiseContractSettings(this.contract.id);
  }

  public changeMarker() {
    if (this.lat && this.lng) {
      if (this.map.hasLayer(this.line)) {
        this.map.removeLayer(this.arrowHead);
        this.map.removeLayer(this.line);
      }
      this.control.setWaypoints([L.latLng(this.lat, this.lng)]);
    }
  }

  public addLane() {
    if (!this.location.siterecordCharacteristics) {
      this.location.siterecordCharacteristics = [];
    }

    const characteristics = new SiterecordCharacteristics();
    characteristics.characteristicsOrder = this.location.siterecordCharacteristics.length + 1;
    if (this.contractSettings) {
      if (this.contractSettings.pointAccuracy) {
        characteristics.accuracy = this.contractSettings.pointAccuracy;
      }
      if (this.contractSettings.pointPeriod) {
        characteristics.period = this.contractSettings.pointPeriod;
      }
      if (this.contractSettings.measurementCategory) {
        characteristics.measurementCategory = this.contractSettings.measurementCategory;
      }
    }

    if (
      this.location.siterecordCharacteristics.length > 0 &&
      this.location.siterecordCharacteristics[0].measurementCategory
    ) {
      characteristics.measurementCategory = this.location.siterecordCharacteristics[0].measurementCategory;
    }
    this.location.siterecordCharacteristics.push(characteristics);

    this.setNumberOfLanes();
  }

  public removeLane(): void {
    this.location.siterecordCharacteristics.pop();
    if (this.location.siterecordCharacteristics.length === 0) {
      this.location.siterecordCharacteristics = null;
    }

    this.setNumberOfLanes();
  }

  public setNumberOfLanes() {
    if (this.location.siterecordCharacteristics) {
      const specificLanes = this.location.siterecordCharacteristics.map(data => data.specificLane);
      const uniqueLanes = specificLanes.filter((x, i, a) => x && a.indexOf(x) === i);

      this.location.numberOfLanes = uniqueLanes.length;
    } else {
      this.location.numberOfLanes = 0;
    }
  }

  public addSiterecordIntersections(type: string): void {
    const siterecordIntersections = this.location.siterecordIntersections;
    if (!siterecordIntersections) {
      this.location.siterecordIntersections = [];
    }

    const intersections = new SiterecordIntersections();
    if (type === 'maxLineupLengthAvailable') {
      const maxLineupLengthAvailable = this.siterecordIntersectionsMaxLineupLengthAvailable;
      if (!maxLineupLengthAvailable) {
        this.siterecordIntersectionsMaxLineupLengthAvailable = [];
      }
      intersections.intersectionsOrder = this.location.siterecordIntersections.length + 1;
      this.siterecordIntersectionsMaxLineupLengthAvailable.push(intersections);
    } else if (type === 'trafficlightSignalGroup') {
      const trafficlightSignalGroups = this.siterecordIntersectionsTrafficlightSignalGroups;
      if (!trafficlightSignalGroups) {
        this.siterecordIntersectionsTrafficlightSignalGroups = [];
      }
      intersections.intersectionsOrder = this.location.siterecordIntersections.length + 1;
      this.siterecordIntersectionsTrafficlightSignalGroups.push(intersections);

      const characteristicsExtended = new SiterecordCharacteristicsExtended();
      const intersectionData = new IntersectionData();
      intersectionData.intersectionDataOrder = 1;
      characteristicsExtended.intersectionData = [intersectionData];
      const characteristics = new SiterecordCharacteristics();
      characteristics.siterecordCharacteristicsExtended = characteristicsExtended;
      if (!this.location.siterecordCharacteristics) {
        characteristics.characteristicsOrder = 1;
        this.location.siterecordCharacteristics = [characteristics];
      } else {
        characteristics.characteristicsOrder = this.location.siterecordCharacteristics.length + 1;
        this.location.siterecordCharacteristics.push(characteristics);
      }

      const index = this.siterecordIntersectionsTrafficlightSignalGroups.length - 1;
      this.addIntersectionData(index, this.location.siterecordCharacteristics[index], 'specificLaneComposition');
      this.addIntersectionData(index, this.location.siterecordCharacteristics[index], 'trafficSortingCharacteristics');
    } else if (type === 'trafficSortingCharacteristics') {
      const trafficSortingCharacteristics = this.siterecordIntersectionsTrafficSortingCharacteristics;
      if (!trafficSortingCharacteristics) {
        this.siterecordIntersectionsTrafficSortingCharacteristics = [];
      }
      intersections.intersectionsOrder = this.location.siterecordIntersections.length + 1;
      this.siterecordIntersectionsTrafficSortingCharacteristics.push(intersections);
    }
    this.location.siterecordIntersections.push(intersections);
  }

  public removeSiterecordIntersections(type: string): void {
    let intersections;
    if (type === 'trafficlightSignalGroup') {
      intersections = this.siterecordIntersectionsTrafficlightSignalGroups.pop();

      const index = this.siterecordIntersectionsTrafficlightSignalGroups.length;
      this.removeIntersectionData(index, this.location.siterecordCharacteristics[index], 'intersectionData');
      this.removeIntersectionData(index, this.location.siterecordCharacteristics[index], 'specificLaneComposition');
      this.removeIntersectionData(
        index,
        this.location.siterecordCharacteristics[index],
        'trafficSortingCharacteristics'
      );
    } else if (type === 'trafficSortingCharacteristics') {
      intersections = this.siterecordIntersectionsTrafficSortingCharacteristics.pop();
      this.intersectionsTrafficSortingCharacteristics.splice(
        this.intersectionsTrafficSortingCharacteristics.indexOf(intersections.trafficSortingCharacteristics),
        1
      );

      for (const idts of this.intersectionDataTrafficSortingCharacteristics) {
        if (idts.length > this.intersectionsTrafficSortingCharacteristics.length) {
          idts.pop();
        }
      }

      if (this.siterecordIntersectionsTrafficSortingCharacteristics.length === 0) {
        this.siterecordIntersectionsTrafficSortingCharacteristics = null;
      }
    }

    if (intersections) {
      const siterecordIntersections = this.location.siterecordIntersections;
      siterecordIntersections.splice(siterecordIntersections.indexOf(intersections), 1);

      if (siterecordIntersections.length === 0) {
        this.location.siterecordIntersections = null;
      }
    }
  }

  public measurementValueTypeChanged(index: number, measurementValueType: string) {
    if (measurementValueType === 'trafficStatusInformation') {
      if (!this.location.siterecordCharacteristics[index].siterecordCharacteristicsExtended.queueCharacteristics) {
        this.addQueueCharacteristics(index, this.location.siterecordCharacteristics[index], 'queueCharacteristics');
      }
    } else if (this.location.siterecordCharacteristics[index].siterecordCharacteristicsExtended.queueCharacteristics) {
      this.removeQueueCharacteristics(index, this.location.siterecordCharacteristics[index], 'queueCharacteristics');
    }
  }

  public equipmentTypeChanged(equipmentType: string) {
    if (equipmentType === 'vri' && !this.isEquipmentTypeVRI) {
      this.isEquipmentTypeVRI = true;
      this.location.siterecordCharacteristics = null;
      this.addCharacteristics();
    } else if (this.isEquipmentTypeVRI) {
      this.isEquipmentTypeVRI = false;
      this.removeCharacteristics();
      this.addLane();
    }
  }

  private addCharacteristics(): void {
    if (!this.location.siterecordIntersections) {
      this.addSiterecordIntersections('maxLineupLengthAvailable');
      this.addSiterecordIntersections('trafficlightSignalGroup');
      this.addSiterecordIntersections('trafficSortingCharacteristics');
    }
  }

  private removeCharacteristics(): void {
    if (this.location.siterecordCharacteristics) {
      this.location.siterecordCharacteristics = null;
    }
    if (this.location.siterecordIntersections) {
      this.location.siterecordIntersections = null;
    }

    this.siterecordIntersectionsTrafficlightSignalGroups = [];
    this.siterecordIntersectionsTrafficSortingCharacteristics = [];
    this.siterecordIntersectionsMaxLineupLengthAvailable = [];
    this.queueCharacteristicsQueueTypes = [[]];
    this.queueCharacteristicsQueueDetectionMethods = [[]];
    this.intersectionDataSpecificLaneCompositions = [[]];
    this.intersectionDataTrafficSortingCharacteristics = [[]];
  }

  public hasMultipleLanes(): boolean {
    return this.location.siterecordCharacteristics.length > 1;
  }

  public hasQueueCharacteristics(): boolean {
    const characteristics = this.location.siterecordCharacteristics[0];
    return (
      characteristics &&
      characteristics.siterecordCharacteristicsExtended &&
      characteristics.siterecordCharacteristicsExtended.queueCharacteristics &&
      characteristics.siterecordCharacteristicsExtended.queueCharacteristics.length > 0
    );
  }

  public hasMaxQueueTypes(): boolean {
    return this.queueCharacteristicsQueueTypes.length === this.queueTypes.length;
  }

  public hasMaxQueueDetectionMethods(): boolean {
    return this.queueCharacteristicsQueueDetectionMethods.length === this.queueDetectionMethodTypes.length;
  }

  public hasMaxSpecificLaneCompositionTypes(): boolean {
    return this.intersectionDataSpecificLaneCompositions.length === this.specificLaneCompositionTypes.length;
  }

  public hasMaxTrafficSortingCharacteristics(index: number): boolean {
    return (
      this.intersectionDataTrafficSortingCharacteristics[index].length <
      this.intersectionsTrafficSortingCharacteristics.length
    );
  }

  public addQueueCharacteristics(
    index: number,
    siterecordCharacteristics: SiterecordCharacteristics,
    type: string
  ): void {
    const queueCharacteristics = new QueueCharacteristics();
    if (type === 'queueCharacteristics') {
      if (!siterecordCharacteristics.siterecordCharacteristicsExtended) {
        siterecordCharacteristics.siterecordCharacteristicsExtended = new SiterecordCharacteristicsExtended();
      }
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics = [];

      const queueTypes = new QueueCharacteristics();
      const queueDetectionMethodTypes = new QueueCharacteristics();

      if (!this.queueCharacteristicsQueueTypes[index]) {
        this.queueCharacteristicsQueueTypes[index] = [];
      }
      queueTypes.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueTypes[index].push(queueTypes);

      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueTypes);

      if (!this.queueCharacteristicsQueueDetectionMethods[index]) {
        this.queueCharacteristicsQueueDetectionMethods[index] = [];
      }
      queueDetectionMethodTypes.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueDetectionMethods[index].push(queueDetectionMethodTypes);

      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueDetectionMethodTypes);
    } else if (type === 'queueType') {
      if (!this.queueCharacteristicsQueueTypes[index]) {
        this.queueCharacteristicsQueueTypes[index] = [];
      }
      queueCharacteristics.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueTypes[index].push(queueCharacteristics);
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueCharacteristics);
    } else if (type === 'queueDetectionMethodType') {
      if (!this.queueCharacteristicsQueueDetectionMethods[index]) {
        this.queueCharacteristicsQueueDetectionMethods[index] = [];
      }
      queueCharacteristics.queueCharacteristicsOrder =
        siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.length + 1;
      this.queueCharacteristicsQueueDetectionMethods[index].push(queueCharacteristics);
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics.push(queueCharacteristics);
    }
  }

  public removeQueueCharacteristics(
    index: number,
    siterecordCharacteristics: SiterecordCharacteristics,
    type: string
  ): void {
    let queueCharacteristics;
    if (type === 'queueCharacteristics') {
      siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics = null;

      this.queueCharacteristicsQueueTypes = [[]];
      this.queueCharacteristicsQueueDetectionMethods = [[]];

      siterecordCharacteristics.siterecordCharacteristicsExtended.timeToForecastPeriodStart = null;
    } else if (type === 'queueType') {
      queueCharacteristics = this.queueCharacteristicsQueueTypes[index].pop();
    } else if (type === 'queueDetectionMethodType') {
      queueCharacteristics = this.queueCharacteristicsQueueDetectionMethods[index].pop();
    }

    if (queueCharacteristics) {
      const queue = siterecordCharacteristics.siterecordCharacteristicsExtended.queueCharacteristics;
      queue.splice(queue.indexOf(queueCharacteristics), 1);
    }
  }

  public addIntersectionData(index: number, siterecordCharacteristics: SiterecordCharacteristics, type: string): void {
    const intersectionData = new IntersectionData();
    if (type === 'specificLaneComposition') {
      if (!this.intersectionDataSpecificLaneCompositions[index]) {
        this.intersectionDataSpecificLaneCompositions[index] = [];
      }
      this.intersectionDataSpecificLaneCompositions[index].push(intersectionData);
    } else if (type === 'trafficSortingCharacteristics') {
      if (!this.intersectionDataTrafficSortingCharacteristics[index]) {
        this.intersectionDataTrafficSortingCharacteristics[index] = [];
      }
      this.intersectionDataTrafficSortingCharacteristics[index].push(intersectionData);
    }

    if (!siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData) {
      siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData = [intersectionData];
    }
    intersectionData.intersectionDataOrder =
      siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData.length + 1;
    siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData.push(intersectionData);
  }

  public removeIntersectionData(
    index: number,
    siterecordCharacteristics: SiterecordCharacteristics,
    type: string
  ): void {
    let intersectionData;
    if (type === 'intersectionData') {
      siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData = null;
      this.intersectionDataSpecificLaneCompositions = [[]];
      this.intersectionDataTrafficSortingCharacteristics = [[]];
    } else if (type === 'specificLaneComposition') {
      intersectionData = this.intersectionDataSpecificLaneCompositions[index].pop();
    } else if (type === 'trafficSortingCharacteristics') {
      intersectionData = this.intersectionDataTrafficSortingCharacteristics[index].pop();
    }

    if (intersectionData) {
      const data = siterecordCharacteristics.siterecordCharacteristicsExtended.intersectionData;
      data.splice(data.indexOf(intersectionData), 1);
    }
  }

  public setRecordIdPrefix(prefix: string): void {
    if (prefix) {
      this.prefix = prefix;
      const recordId = this.location.recordId;
      this.location.recordId =
        recordId == null || recordId === ''
          ? `${this.prefix}_`
          : this.recordId
          ? `${this.prefix}_${this.recordId}`
          : `${this.prefix}_`;
    }
  }

  public setRecordId(): void {
    const recordId = this.location.recordId;
    this.location.recordId =
      recordId == null || recordId === ''
        ? this.recordId
        : this.prefix
        ? `${this.prefix}_${this.recordId}`
        : this.recordId;
  }

  onSubmit() {
    this.isSaving = true;
    this.errorMessages = [];

    this.siterecordService.saveSiterecord(this.location).subscribe(
      response => {
        if (response.ok) {
          if (this.newLocationAfterSave) {
            this.router.navigate([`/location/${this.contract.id}/point/new/${this.lat}/${this.lng}`]);
          } else {
            this.router.navigate([`/locations/${this.contract.id}`]);
          }
        }
        this.isSaving = false;
      },
      (errors: CBMError[]) => {
        if (errors.length === 1 && errors[0].code === '500') {
          alert(this.failedSaveTranslation);
        } else {
          this.errorMessages = errors;
        }
        this.isSaving = false;
      }
    );
  }

  public getDisabledLanes(selectedLane: string): string[] {
    const disabledLanes: string[] = [];
    let selectedLanes: string[] = [];
    this.location.siterecordCharacteristics.map(sc => {
      if (sc.specificLane) {
        selectedLanes.push(sc.specificLane);
      }
    });

    selectedLanes = selectedLanes.sort();
    for (let i = 0; i < selectedLanes.length - 1; i++) {
      if (selectedLanes[i + 1] === selectedLanes[i]) {
        disabledLanes.push(selectedLanes[i]);
      }
    }
    return disabledLanes;
  }

  public getDisabledMeasurementValuetypes(characteristics: SiterecordCharacteristics) {
    const disabledMeasurementValuetypes: string[] = [];
    const selectedLane = characteristics.specificLane;

    this.location.siterecordCharacteristics.map(sc => {
      if (sc.specificLane && sc.specificLane === selectedLane) {
        disabledMeasurementValuetypes.push(sc.measurementValueType);
      }
    });

    return disabledMeasurementValuetypes;
  }

  public onChangeMeasurementCategory(selectedMeasurementCategory: string) {
    this.location.siterecordCharacteristics.map(sc => (sc.measurementCategory = selectedMeasurementCategory));
  }

  public setIntersectionDataTrafficLight(index: number) {
    this.location.siterecordCharacteristics[
      index
    ].siterecordCharacteristicsExtended.intersectionData[0].trafficlightSignalGroup =
      this.siterecordIntersectionsTrafficlightSignalGroups[index].trafficlightSignalGroup;
  }
  public setIntersectionsTrafficSortingCharacteristics(index: number) {
    this.intersectionsTrafficSortingCharacteristics[index] =
      this.siterecordIntersectionsTrafficSortingCharacteristics[index].trafficSortingCharacteristics;
  }

  public setMaxLineupLength() {
    if (this.siterecordIntersectionsMaxLineupLengthAvailable[0].maxLineupLengthAvailable) {
      this.location.siterecordIntersections.push(this.siterecordIntersectionsMaxLineupLengthAvailable[0]);
    } else {
      this.location.siterecordIntersections.splice(
        this.location.siterecordIntersections.indexOf(this.siterecordIntersectionsMaxLineupLengthAvailable[0]),
        1
      );
    }
  }

  public onCancel(): void {
    this.router.navigate([`/locations/${this.location.contractId}`]);
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/insert/point/point-insert.component.ts